import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { useMcpCallQuery } from '../mcp-info.query';
import { CONTENT_WITHHELD_LABEL } from '../mcp-info.types';

import { McpInfoStatusBadge } from './mcp-info-status-badge';

function KeyValue({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <XStack className="justify-between items-baseline py-0.5">
      <span className="text-xs text-foreground-muted shrink-0">{label}</span>
      <span
        className={cn(
          'text-xs text-foreground text-right truncate ml-3',
          mono && 'font-mono tabular-nums'
        )}
      >
        {value}
      </span>
    </XStack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <YStack className="pt-2 gap-0.5">
      <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
        {title}
      </span>
      {children}
    </YStack>
  );
}

function PreBlock({ text }: { text: string }) {
  return (
    <pre className="text-xs font-mono text-foreground-muted whitespace-pre-wrap break-words bg-surface rounded p-2 max-h-[280px] overflow-y-auto">
      {text}
    </pre>
  );
}

interface McpInfoDetailPanelProps {
  callId: string;
  onClose: () => void;
}

export function McpInfoDetailPanel({
  callId,
  onClose,
}: McpInfoDetailPanelProps) {
  const { data: call, isLoading } = useMcpCallQuery(callId);

  return (
    <YStack className="p-3 gap-1">
      <XStack className="justify-between items-center pb-2 border-b border-border">
        <XStack className="items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-foreground truncate">
            {call?.tool_name ?? 'MCP call'}
          </span>
          {call && <McpInfoStatusBadge status={call.status} />}
        </XStack>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <Icons.cancel className="h-3.5 w-3.5" />
        </Button>
      </XStack>

      {!call ? (
        <span className="text-xs text-foreground-muted py-2">
          {isLoading ? 'Loading call...' : 'Call not found.'}
        </span>
      ) : (
        <>
          <Section title="Overview">
            <KeyValue
              label="Time"
              value={new Date(call.created_at).toLocaleString('en-US', {
                hour12: false,
              })}
              mono
            />
            <KeyValue label="Client" value={call.client_name || '-'} />
            <KeyValue label="Client ID" value={call.client_id || '-'} mono />
            <KeyValue
              label="Latency"
              value={
                call.execution_time_ms === null
                  ? '-'
                  : `${call.execution_time_ms}ms`
              }
              mono
            />
            {call.trace_url && (
              <a
                href={call.trace_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline py-0.5"
              >
                Open trace
              </a>
            )}
          </Section>

          {call.content_withheld ? (
            <Section title="Content">
              <span className="text-xs text-foreground-muted">
                {CONTENT_WITHHELD_LABEL}
              </span>
            </Section>
          ) : (
            <>
              <Section title="Arguments">
                <PreBlock text={JSON.stringify(call.arguments, null, 2)} />
              </Section>

              {call.error_message && (
                <Section title="Error">
                  <PreBlock text={call.error_message} />
                </Section>
              )}

              <Section title="Result">
                <PreBlock text={call.result_text || '(empty)'} />
              </Section>
            </>
          )}
        </>
      )}
    </YStack>
  );
}
